import { ownEntries } from '#library/primitive.library.js';
import { isDefined, isCallable, isSymbol } from '#library/assertion.library.js';
const AGO_HENCE_RE = /\b(ago|hence|from\s+now|prior)\b/i;
const ERA_RE = /(?:^|\s)(b\.?c\.?(?:e\.?)?|a\.?d\.?|c\.?e\.?)(?:\s|$)/i;
const CLASS_CACHE_LIMIT = 256;
const classCache = new Map();
/**
 * Memoized wrapper for classifyParseInput to accelerate parse input classification loops.
 */
function classifyParseInputCached(value) {
    const key = String(value ?? '').trim();
    const cached = classCache.get(key);
    if (cached)
        return cached;
    const cls = classifyParseInput(key);
    classCache.set(key, cls);
    if (classCache.size > CLASS_CACHE_LIMIT) {
        const oldest = classCache.keys().next().value;
        if (oldest !== undefined)
            classCache.delete(oldest);
    }
    return cls;
}
const LAYOUT = {
    hms: 'hourMinuteSecond',
    dmy6: 'dayMonthYearShort',
    mdy6: 'monthDayYearShort',
    ymd6: 'yearMonthDayShort',
    wkd: 'weekDay',
    dt: 'date',
    tm: 'time',
    dtm: 'dateTime',
    tmd: 'timeDate',
    dmy: 'dayMonthYear',
    mdy: 'monthDayYear',
    ymd: 'yearMonthDay',
    off: 'offset',
    rel: 'relativeOffset',
    ye: 'yearEra',
    ey: 'eraYearLeading',
    dRy: 'dayRomanMonthYear',
    Ryy: 'romanYearOnly',
};
const COMPACT_SIX = new Set([LAYOUT.hms, LAYOUT.dmy6, LAYOUT.mdy6, LAYOUT.ymd6]);
const COMPACT_EIGHT = new Set([LAYOUT.dt, LAYOUT.dmy, LAYOUT.mdy, LAYOUT.ymd]);
const ALPHA_EXCLUDE = new Set([LAYOUT.hms, LAYOUT.dmy6, LAYOUT.mdy6, LAYOUT.ymd6, LAYOUT.off]);
const NUMERIC_EXCLUDE = new Set([LAYOUT.wkd, LAYOUT.rel, LAYOUT.dRy, LAYOUT.Ryy]);
const COLON_BIAS = new Set([LAYOUT.tm, LAYOUT.tmd, LAYOUT.dtm]);
const ERA_BIAS = new Set([LAYOUT.ye, LAYOUT.ey, LAYOUT.dmy, LAYOUT.mdy, LAYOUT.ymd, LAYOUT.Ryy]);
/**
 * Classify raw parse input once so later planner phases can choose candidate layouts.
 * Release C planner shell: currently classification is observational only.
 */
export function classifyParseInput(value) {
    const trim = String(value ?? '').trim();
    const length = trim.length;
    let hasDigits = false;
    let hasLetters = false;
    let hasColon = false;
    let hasOther = false;
    for (let i = 0; i < length; i++) {
        const code = trim.charCodeAt(i);
        if (code >= 48 && code <= 57) {
            hasDigits = true;
            continue;
        }
        if ((code >= 65 && code <= 90) || (code >= 97 && code <= 122)) {
            hasLetters = true;
            continue;
        }
        if (code === 58) {
            hasColon = true;
            continue;
        }
        hasOther = true;
    }
    const hasSign = length > 0 && (trim[0] === '+' || trim[0] === '-');
    const isPureNumeric = hasDigits && !hasLetters && !hasOther && !hasColon;
    const isAlphaOnly = hasLetters && !hasDigits && !hasOther && !hasColon;
    const isSixDigits = isPureNumeric && length === 6;
    const isEightDigits = isPureNumeric && length === 8;
    const hasAgoHence = hasLetters && AGO_HENCE_RE.test(trim);
    const hasEra = hasLetters && ERA_RE.test(trim);
    return {
        trim,
        length,
        hasDigits,
        hasLetters,
        hasColon,
        hasSign,
        hasAgoHence,
        isPureNumeric,
        isAlphaOnly,
        isSixDigits,
        isEightDigits,
        hasEra,
    };
}
/** Return layout patterns in resolved layout order, with optional planner pre-filtering. */
export function selectLayoutPatterns(state, value, options = {}) {
    const onPlan = options.onPlan;
    const wantsPlan = isCallable(onPlan);
    const ordered = ownEntries(state.parse.layout)
        .map(([layoutKey]) => {
        const symKey = isSymbol(layoutKey)
            ? layoutKey
            : state.parse.token?.[String(layoutKey)];
        return [symKey, symKey ? state.parse.pattern.get(symKey) : undefined];
    })
        .filter((entry) => isDefined(entry[0]) && isDefined(entry[1]));
    if (options.enablePrefilter !== true) {
        if (wantsPlan) {
            onPlan({
                inputClass: classifyParseInputCached(value),
                totalCandidates: ordered.length,
                selectedCandidates: ordered.length,
                fallbackToFull: false,
                rulesApplied: [],
            });
        }
        return ordered;
    }
    const cls = classifyParseInputCached(value);
    const hasAgoHence = cls.hasAgoHence;
    const isAlphaOnly = cls.isAlphaOnly;
    const isSixDigits = cls.isSixDigits;
    const isEightDigits = cls.isEightDigits;
    const isPureNumeric = cls.isPureNumeric;
    const hasColon = cls.hasColon;
    const hasEra = cls.hasEra;
    let rulesApplied;
    if (wantsPlan) {
        rulesApplied = [];
        if (hasAgoHence)
            rulesApplied.push('hasAgoHence');
        if (isAlphaOnly)
            rulesApplied.push('isAlphaOnly');
        if (isSixDigits)
            rulesApplied.push('isSixDigits');
        if (isEightDigits)
            rulesApplied.push('isEightDigits');
        if (isPureNumeric)
            rulesApplied.push('isPureNumeric');
        if (hasColon)
            rulesApplied.push('hasColon');
        if (hasEra)
            rulesApplied.push('hasEra');
    }
    const selected = [];
    const timeBiased = [];
    const eraBiased = [];
    for (const entry of ordered) {
        const desc = entry[0].description ?? '';
        let include = true;
        if (hasAgoHence) {
            include = desc === LAYOUT.rel || desc === LAYOUT.wkd;
        }
        else {
            if (include && isAlphaOnly && ALPHA_EXCLUDE.has(desc))
                include = false;
            if (include && isSixDigits && !COMPACT_SIX.has(desc))
                include = false;
            if (include && isEightDigits && !COMPACT_EIGHT.has(desc))
                include = false;
            if (include && isPureNumeric && NUMERIC_EXCLUDE.has(desc))
                include = false;
        }
        if (!include)
            continue;
        if (hasEra && ERA_BIAS.has(desc))
            eraBiased.push(entry);
        else if (hasColon && COLON_BIAS.has(desc))
            timeBiased.push(entry);
        else
            selected.push(entry);
    }
    let next = selected;
    if (hasEra && eraBiased.length > 0) {
        next = eraBiased;
        if (hasColon && timeBiased.length > 0) {
            for (const entry of timeBiased)
                next.push(entry);
        }
        for (const entry of selected)
            next.push(entry);
    }
    else if (hasColon && timeBiased.length > 0) {
        next = timeBiased;
        for (const entry of selected)
            next.push(entry);
    }
    // Safety: if a pre-filter rule over-constrains, retain full candidate order.
    if (next.length === 0) {
        if (wantsPlan) {
            onPlan({
                inputClass: cls,
                totalCandidates: ordered.length,
                selectedCandidates: ordered.length,
                fallbackToFull: true,
                rulesApplied: rulesApplied,
            });
        }
        return ordered;
    }
    if (wantsPlan) {
        onPlan({
            inputClass: cls,
            totalCandidates: ordered.length,
            selectedCandidates: next.length,
            fallbackToFull: false,
            rulesApplied: rulesApplied,
        });
    }
    return next;
}
